import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Maximize2, FileText, Loader2, Info } from "lucide-react";
import { hojasService } from "@/services/hojasService";
import { HojaInterna } from "./types";

interface VistaHojasInternasPorCategoriaProps {
  categoria: 'infantil' | 'literario' | 'general';
  hojasIdsEspecificos?: string[]; // Si se especifican, solo mostrar estos IDs
  orientacion?: 'horizontal' | 'vertical'; // Orientación para el aspect ratio de las hojas
}

/**
 * Componente que carga y muestra hojas internas según la categoría del producto
 * Si se especifican hojasIdsEspecificos, solo muestra esos. Si no, muestra todos los activos de la categoría.
 */
export const VistaHojasInternasPorCategoria = ({
  categoria,
  hojasIdsEspecificos,
  orientacion = 'vertical'
}: VistaHojasInternasPorCategoriaProps) => {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    name: string;
    descripcion?: string;
  } | null>(null);

  // Cargar hojas de la categoría
  const { data: hojasCategoria, isLoading } = useQuery({
    queryKey: ['hojas-categoria', categoria],
    queryFn: () => hojasService.getHojasPorCategoria(categoria),
    enabled: categoria === 'infantil' || categoria === 'literario',
    staleTime: 5 * 60 * 1000,
  });

  const handleOpenImageModal = (imageUrl: string, name: string, descripcion?: string) => {
    setSelectedImage({ url: imageUrl, name, descripcion });
    setImageModalOpen(true);
  };

  // Clase de aspecto dinámica según orientación
  const aspectRatioClass = orientacion === 'horizontal'
    ? 'aspect-[4/3]'  // Horizontal: 4 ancho × 3 alto
    : 'aspect-[3/4]';  // Vertical: 3 ancho × 4 alto

  // No mostrar si no es infantil o literario
  if (categoria === 'general') {
    return null;
  }

  // Filtrar hojas: si hay IDs específicos, solo esos; si no, todos
  const hojasAMostrar = hojasIdsEspecificos && hojasIdsEspecificos.length > 0
    ? hojasCategoria?.filter(h => hojasIdsEspecificos.includes(h.id))
    : hojasCategoria;

  // No mostrar si no hay hojas
  if (!hojasAMostrar || hojasAMostrar.length === 0) {
    return null;
  }

  // Determinar título según categoría
  const titulo = categoria === 'infantil'
    ? "📚 Contenido Pediátrico Incluido"
    : categoria === 'literario'
    ? "📖 Contenido Literario Incluido"
    : "📄 Contenido Incluido";

  const descripcion = categoria === 'infantil'
    ? "Estas hojas especializadas vienen incluidas en tu agenda pediátrica"
    : categoria === 'literario'
    ? "Estas hojas especializadas vienen incluidas en tu diario de lectura"
    : "Estas hojas vienen incluidas con tu producto";

  return (
    <>
      <Card className="border-2 border-blue-200/50 bg-blue-50/30">
        <CardHeader className="bg-gradient-to-br from-blue-100/50 to-blue-50/30 p-3 md:p-4 pb-2 md:pb-3">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <div className="flex-1">
              <CardTitle className="font-playfair text-brown-dark text-sm md:text-base">
                {titulo}
              </CardTitle>
              <p className="text-xs text-brown-light/70 mt-0.5">
                {descripcion}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 md:p-4 pt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              {/* Info badge */}
              <div className="mb-2 bg-blue-100/50 border border-blue-200/50 rounded-lg p-2">
                <p className="text-xs text-blue-900/80 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    Click para ver en detalle.
                    <strong className="ml-1">{hojasAMostrar.length} {hojasAMostrar.length === 1 ? 'hoja' : 'hojas'}</strong>
                  </span>
                </p>
              </div>

              {/* Galería horizontal */}
              <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-blue-300/50 scrollbar-track-transparent">
                {hojasAMostrar.map((hoja: HojaInterna) => (
                  <div
                    key={hoja.id}
                    className="group relative flex-shrink-0 w-24 md:w-32 bg-white border-2 border-blue-200/40 rounded-lg overflow-hidden hover:border-blue-400/60 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => handleOpenImageModal(hoja.path, hoja.nombre, hoja.descripcion)}
                  >
                    {/* Imagen */}
                    <div className={`${aspectRatioClass} overflow-hidden bg-blue-50/30 relative`}>
                      <img
                        src={hoja.path}
                        alt={hoja.nombre}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        loading="lazy"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=300&h=400&fit=crop";
                        }}
                      />

                      {/* Icono de ampliar */}
                      <div className="absolute top-1 right-1 md:top-1.5 md:right-1.5 bg-blue-600/80 text-white rounded-full w-5 h-5 md:w-6 md:h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <Maximize2 className="h-2.5 w-2.5 md:h-3 md:w-3" />
                      </div>

                      {/* Badge "Incluido" */}
                      <div className="absolute bottom-1 left-1 bg-blue-600/90 text-white text-[8px] md:text-[10px] font-bold px-1.5 py-0.5 rounded">
                        ✓ INCLUIDO
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal para imagen ampliada */}
      <Dialog open={imageModalOpen} onOpenChange={setImageModalOpen}>
        <DialogContent className="max-w-[95vw] md:max-w-4xl max-h-[95vh] p-3 md:p-6">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-playfair text-brown-dark text-base md:text-lg flex items-center gap-2">
              <FileText className="h-4 w-4 md:h-5 md:w-5 text-blue-600 flex-shrink-0" />
              <span className="truncate">{selectedImage?.name}</span>
            </DialogTitle>
            {selectedImage?.descripcion && (
              <p className="text-xs md:text-sm text-brown-light/70 mt-1 line-clamp-2">
                {selectedImage.descripcion}
              </p>
            )}
          </DialogHeader>
          <div className="flex items-center justify-center" style={{ maxHeight: 'calc(95vh - 120px)' }}>
            {selectedImage && (
              <div className="relative w-full h-full flex items-center justify-center">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.name}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg border-2 border-blue-200/50"
                  style={{
                    maxHeight: 'calc(95vh - 120px)',
                    width: 'auto',
                    height: 'auto'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "https://images.unsplash.com/photo-1557682250-33bd709cbe85?w=800&h=1000&fit=crop";
                  }}
                />
                <div className="absolute top-2 right-2 md:top-3 md:right-3 bg-blue-600 text-white text-[10px] md:text-xs font-bold px-2 md:px-3 py-1 md:py-1.5 rounded-lg shadow-lg">
                  ✓ INCLUIDO
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
